import _ from "lodash";
import React, { useMemo } from "react";
import classes from "./AttachmentsContainer.scss";
import JournalPreviewCard from "AppComponents/Attachment/JournalPreviewCard";
import { MediaCard } from "@toddle-edu/ds-web";
import TextMoreLess from "UIComponents/TextMoreLess";
import {
  getShowAssessmentTool,
  getAssessmentToolTypeForMediaCard,
  reorderTools,
} from "ClassRoom/modules/utils";
import { showNewTheatreMode } from "Utils/GetTheatreModeType";
import { onEnterOrSpaceKeyDown } from "Utils";
import { useKeyDownWrapper } from "Post/hooks";

const BLANK_ARRAY = [];
export const AttachmentsContainer = props => {
  const {
    evaluationContent,
    setAssessmentToolInfo,
    setShowAssessmentTool,
    assessmentFields,
    commentMessage,
    t,
    onClickMedia,
    setTheatreAttachmentIndex,
    theatreActiveAttachmentId,
    setTheatreAttachmentById,
    parentId,
    postAttachments = [],
    attachments = BLANK_ARRAY,
    shouldShowEvaluationTools,
  } = props;

  const updatedAttachments = useMemo(() => {
    return [
      ..._.flatten(
        _.map(attachments, attachmentGroup =>
          _.get(attachmentGroup, "attachments", [])
        )
      ),
      ...postAttachments,
    ];
  }, [attachments, postAttachments]);

  const responseCaption = _.get(props, "responseCaption", "");
  const attachmentsFromGroups = _.map(
    _.get(props, "attachments", []),
    attachmentGroup => _.get(attachmentGroup, "attachments[0]")
  );

  const onMediaClick = ({ event, index }) => {
    if (
      _.get(props, "isSidePaneContent", false) &&
      showNewTheatreMode() &&
      _.isFunction(setTheatreAttachmentIndex)
    ) {
      setTheatreAttachmentIndex(index);
    } else {
      onClickMedia({
        event,
        attachmentData: updatedAttachments,
        activeIndex: index,
      });
    }
  };

  const handleMediaClick = index => event => onMediaClick({ event, index });

  const memoizedOnMediaClick = _.memoize((event, index) => () =>
    onMediaClick({ event, index })
  );

  const handleMediaKeyDown = index => event =>
    onEnterOrSpaceKeyDown(event, memoizedOnMediaClick(event, index));

  const onLearningGoalsCardClick = e => {
    if (showNewTheatreMode() && _.isFunction(setTheatreAttachmentById)) {
      setTheatreAttachmentById({
        id: parentId + "_LEARNING_GOALS",
        type: "ASSESSMENT_TOOL",
        customCompareFn: ({ item, id }) => item?.id == id,
      });
    } else {
      if (_.has(props, "setShowRateLearningGoalsModal"))
        props.setShowRateLearningGoalsModal(true);
    }
  };

  const handleLearningGoalsKeydown = useKeyDownWrapper(
    onLearningGoalsCardClick
  );

  return _.get(props, "isSidePaneContent", false) ? (
    <div className={classes.smallAttachmentsContainer}>
      {_.get(responseCaption, "length", 0) > 0 && (
        <div className={classes.response}>
          <TextMoreLess value={responseCaption} minHeight={48} />
        </div>
      )}
      {_.map(updatedAttachments, (attachment, index) => {
        const attachmentData = attachment;
        const attachmentType = _.get(attachmentData, "type", "FILE");
        const isToddleDoc = attachmentType === "TODDLE_DOC";
        const name = _.get(attachmentData, "name", "untitled");
        const mimeType = _.get(attachmentData, "mimeType", "");
        const thumbUrl = isToddleDoc
          ? undefined
          : attachmentType === "IMAGE"
          ? _.get(attachmentData, "url", "")
          : _.get(attachmentData, "thumbUrl", "");
        const url = _.get(attachmentData, "url", "");
        const subText = isToddleDoc
          ? t("classRoom:toddle_doc_capitalized")
          : undefined;

        return (
          <div
            tabIndex={0}
            className={classes.mediaCardContainer}
            onKeyDown={handleMediaKeyDown?.(index)}
          >
            <MediaCard
              key={`ATTACHMENT_${_.get(attachment, "id", "")}`}
              cardType="medium"
              name={name}
              type={isToddleDoc ? "TODDLE-DOC" : attachmentType}
              mimeType={mimeType}
              thumbUrl={thumbUrl}
              url={url}
              showSubtext={true}
              subtext={subText}
              onClick={handleMediaClick?.(index)}
              isActivated={
                _.get(attachmentData, "id", "") == theatreActiveAttachmentId
              }
            />
          </div>
        );
      })}
      {(_.get(props, "hasAssesmentTool", false) ||
        _.get(props, "hasLearningGoals", false)) && (
        <div className={classes.studentEvaluationLabel}>
          {t("common:evaluation")}
        </div>
      )}
      {_.get(props, "hasAssesmentTool", false) &&
        _.map(
          reorderTools({
            assessmentTools: _.get(evaluationContent, "assessmentTools", []),
          }),
          ({ node }, index) => {
            const assessmentTool = _.get(node, "assessmentToolItem", {});
            const assessmentToolId = _.get(node, "assessmentToolItem.id", "");
            const assessmentToolType = _.get(node, "assessmentToolType", "");

            // do not show score and comment card
            if (_.includes(["COMMENT"], assessmentToolType)) return;

            const isTeacherEvaluationEnabled = _.get(
              node,
              "isTeacherEvaluationEnable",
              false
            );
            const isStudentEvaluationEnabled = _.get(
              node,
              "isStudentEvaluationEnable",
              false
            );

            const onClickAssessmentToolCard = () => {
              if (showNewTheatreMode()) {
                const indexOfCurrentNode = _.findIndex(
                  _.filter(
                    reorderTools({
                      assessmentTools: evaluationContent?.assessmentTools,
                      hasEdges: true,
                    }),
                    ({ node: assessmentNode }) =>
                      !_.includes(
                        ["COMMENT"],
                        assessmentNode?.assessmentToolType
                      )
                  ),
                  ({ node: nodeToFind }) => nodeToFind.id == node.id
                );
                onMediaClick({
                  index:
                    Math.max(_.get(props, "attachments.length", 0), 0) +
                    Math.max(indexOfCurrentNode, 0),
                });
              } else {
                setShowAssessmentTool(true);
                if (setAssessmentToolInfo) {
                  setAssessmentToolInfo({
                    assessmentToolIdLocal: assessmentToolId,
                    assessmentToolTypeLocal: assessmentToolType,
                    isTeacherEvaluationEnabledLocal: isTeacherEvaluationEnabled,
                    isStudentEvaluationEnabledLocal: isStudentEvaluationEnabled,
                  });
                }
              }
            };

            const showAssessmentToolCard =
              getShowAssessmentTool({
                assessmentTool,
                assessmentToolType,
                allFields: assessmentFields,
              }) &&
              (isTeacherEvaluationEnabled || isStudentEvaluationEnabled);

            const {
              type,
              subtext = "",
              icon,
            } = getAssessmentToolTypeForMediaCard({
              assessmentToolType,
              t,
            });

            const handleAssessmentKeydown = e =>
              onEnterOrSpaceKeyDown(e, onClickAssessmentToolCard);

            const mediaCardName =
              isTeacherEvaluationEnabled && isStudentEvaluationEnabled // both evaluation
                ? t("common:evaluation")
                : isTeacherEvaluationEnabled && !isStudentEvaluationEnabled // only teacher evaluation
                ? t("common:teacher_evaluation")
                : !isTeacherEvaluationEnabled && isStudentEvaluationEnabled // only student evaluation
                ? t("common:student_evaluation")
                : t("common:evaluation"); // defensive: if both value got false, then show only "Evaluation"
            return (
              showAssessmentToolCard && (
                <div
                  tabIndex={0}
                  className={classes.mediaCardContainer}
                  onKeyDown={handleAssessmentKeydown}
                >
                  <MediaCard
                    key={`ASSESSMENT_TOOL_${assessmentToolId}`}
                    cardType="medium"
                    name={mediaCardName}
                    type={type}
                    showSubtext={true}
                    onClick={onClickAssessmentToolCard}
                    subtext={subtext}
                    isActivated={assessmentToolId == theatreActiveAttachmentId}
                    icon={icon}
                  />
                </div>
              )
            );
          }
        )}
      {props.hasLearningGoals && (
        <div
          tabIndex={0}
          className={classes.mediaCardContainer}
          onKeyDown={handleLearningGoalsKeydown}
        >
          <MediaCard
            cardType="medium"
            type={"LEARNING GOALS"}
            name={"Rate on learning goals"}
            isActivated={
              theatreActiveAttachmentId == parentId + "_LEARNING_GOALS"
            }
            showSubtext={true}
            onClick={onLearningGoalsCardClick}
          />
        </div>
      )}
      {commentMessage && shouldShowEvaluationTools && (
        <div className={classes.teacherRemarkContainer}>
          <div className={"text-body-2"}>{commentMessage}</div>
        </div>
      )}
    </div>
  ) : (
    <div
      className={classes.attachmentsContainer}
      style={_.get(props, "attachmentsContainerStyle", {})}
    >
      {_.map(updatedAttachments, (attachment, index) => {
        return (
          <JournalPreviewCard
            key={_.get(attachment, "id", "")}
            attachment={attachment}
            itemStyles={_.get(props, "itemStyles", {})}
            cardStyle={_.get(props, "cardStyle", {})}
            containerStyle={_.get(props, "containerStyle", {})}
            isStudentResponse={_.get(props, "isStudentResponse", false)}
            onClickMedia={event => {
              onClickMedia({
                event,
                attachmentData: updatedAttachments,
                activeIndex: index,
              });
            }}
          />
        );
      })}
    </div>
  );
};
